import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, BackHandler, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useAuthStore } from '@/store/auth';
import { useAuthHydrated } from '@/hooks/useRequireAuth';
import { useTranslation } from '@/hooks/useTranslation';
import { ThemeProvider } from '@/context/ThemeContext';
import { ModalBase } from '@/components/Modal/ModalRoot';
import Text from '@/components/Typography/Text';

const VerifyEmailModal = React.lazy(() => import('@/components/Modal/VerifyEmailModal'));
const keepOpen = () => {};

function LoadingAccount() {
  return <View style={styles.loading} accessibilityRole="progressbar" accessibilityLabel="Opening Gab AI"><ActivityIndicator color="#6E6E73" /></View>;
}

// Saved flags can predate verification or a Plus purchase on another device.
// Confirm them with the server before telling someone they must verify.
function ResolveAccount({ children }) {
  const { t } = useTranslation();
  const fetchUserSlim = useAuthStore(state => state.fetchUserSlim);
  const [failed, setFailed] = useState(false);
  const [resolved, setResolved] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setFailed(false);
    Promise.resolve().then(() => fetchUserSlim()).then(user => {
      if (!active) return;
      if (user?.id) setResolved(true);
      else if (useAuthStore.getState().token) setFailed(true);
    }).catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [fetchUserSlim, attempt]);
  if (resolved) return children;
  if (!failed) return <LoadingAccount />;
  return <View style={styles.loading}>
    <Text style={styles.message}>{t('modals.verifyEmail.clean.loadError')}</Text>
    <Pressable accessibilityRole="button" onPress={() => setAttempt(value => value + 1)} style={styles.retry}><Text style={styles.retryText}>{t('modals.verifyEmail.clean.retry')}</Text></Pressable>
    <Pressable accessibilityRole="button" onPress={() => useAuthStore.getState().clearSession()} style={styles.signOut}><Text style={styles.message}>{t('modals.verifyEmail.clean.signOut')}</Text></Pressable>
  </View>;
}

function RequiredVerification({ userId }) {
  const signOut = useCallback(() => useAuthStore.getState().clearSession(), []);
  useEffect(() => {
    if (Platform.OS !== 'android') return undefined;
    const subscription = BackHandler.addEventListener('hardwareBackPress', () => true);
    return () => subscription.remove();
  }, []);
  return <View testID="email-verification-gate" style={styles.surface}>
    <ModalBase isOpen onClose={keepOpen} width={460} height={780} heightMobile={[780, '96%']}>
      <Suspense fallback={<LoadingAccount />}>
        <VerifyEmailModal key={userId} required source="signup_verification" onSignOut={signOut} />
      </Suspense>
    </ModalBase>
  </View>;
}

// Keep the app itself unmounted until verification, including its keyboard
// shortcuts, pending auto-sends and native workers. Guests can still browse;
// Plus and already-verified email/SSO accounts proceed without any setup.
export default function EmailVerificationGate({ children }) {
  const hydrated = useAuthHydrated();
  const token = useAuthStore(state => state.token);
  const user = useAuthStore(state => state.user);
  const signingOut = useAuthStore(state => state._isSigningOut);
  const needsVerification = !user?.flags?.isPlus && !user?.flags?.isGhost && user?.email && !user.flags?.isEmailVerified;
  let gate;
  if (!hydrated) gate = <LoadingAccount />;
  else if (token && !signingOut && (!user?.id || needsVerification)) gate = <ResolveAccount key={token}>
    <RequiredVerification key={user?.id} userId={user?.id} />
  </ResolveAccount>;
  else return children;
  return <ThemeProvider forceTheme="white" scoped>{gate}</ThemeProvider>;
}

const styles = StyleSheet.create({
  surface: { flex: 1, backgroundColor: '#F5F5F7' },
  loading: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32, gap: 20, backgroundColor: '#FFFFFF' },
  message: { color: '#6E6E73', fontSize: 14, lineHeight: 21, textAlign: 'center' },
  retry: { minHeight: 48, paddingHorizontal: 28, paddingVertical: 14, borderRadius: 14, backgroundColor: '#1D1D1F' },
  retryText: { color: '#FFFFFF', fontSize: 15 },
  signOut: { minHeight: 44, justifyContent: 'center' },
});
